import React, { useState, type PropsWithChildren } from 'react';
import { Formik } from 'formik';
import { GoogleLogin } from '@react-oauth/google';
import '../styles.scss';
import AuthorizationLayout from '../components/AuthorizationLayout';
import { useTranslation } from 'react-i18next';
import eyeSVG from '../../../../assets/images/svgs/eye.svg';
import hideEyeSVG from '../../../../assets/images/svgs/hide-eye.svg';
import { splitEmailOrPhone } from '../../application/splitEmailOrPhone';
import { readApiError } from '../../application/readApiError';
import {
  useSignInMutation,
  useSignInWithGoogleMutation,
} from '../../infrastructure/api/auth.api';
import { paths } from '../../../../app/routes/paths';
import { showErrorToast } from '../../../../app/components/SnackBar';

const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const SignIn = (props: PropsWithChildren) => {
  const { t } = useTranslation();
  const [signIn] = useSignInMutation();
  const [signInWithGoogle] = useSignInWithGoogleMutation();
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (data, { setSubmitting }) => {
    const identity = splitEmailOrPhone(data.emailOrPhone);
    if (!identity?.email) {
      setSubmitting(false);
      showErrorToast(t('form.invalid_email_or_phone'));
      return;
    }

    try {
      await signIn({
        email: identity.email,
        password: data.password,
      }).unwrap();
    } catch (error) {
      const apiError = readApiError(error);
      if (apiError.code === 'UNAUTHORIZED' || apiError.statusCode === 401) {
        showErrorToast(t('form.invalid_credentials', { ns: 'errors' }));
      } else {
        showErrorToast(apiError.message || t('form.server_error'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: {
    credential?: string;
  }) => {
    const idToken = credentialResponse?.credential;
    if (!idToken) {
      showErrorToast(t('form.server_error'));
      return;
    }

    try {
      await signInWithGoogle({ idToken }).unwrap();
    } catch (error) {
      const apiError = readApiError(error);
      showErrorToast(apiError.message || t('form.server_error'));
    }
  };

  const handleGoogleError = () => {
    showErrorToast(t('form.server_error'));
  };

  return (
    <AuthorizationLayout {...props}>
      <div className={'sign-in border br-16 p-16'}>
        <div className={`auth-data-wrapper`}>
          <div className="block-title fs-3 mb-4 mt-3">{t('sign_in.title')}</div>
          <Formik<any>
            initialValues={{ emailOrPhone: '', password: '' }}
            validate={(values) => {
              const errors: any = {};
              if (!values.emailOrPhone) {
                errors.emailOrPhone = t('form.required');
              } else if (
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.emailOrPhone) &&
                !/^\+?[1-9]\d{1,14}$/.test(values.emailOrPhone)
              ) {
                errors.emailOrPhone = t('form.invalid_email_or_phone');
              }
              if (!values.password) errors.password = t('form.required');
              return errors;
            }}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="custom-form-group auth-form-group d-flex flex-column">
                      <input
                        className={`form-control custom-form-control auth-form-control ${errors.emailOrPhone && touched.emailOrPhone ? 'is-invalid' : ''}`}
                        type="text"
                        name="emailOrPhone"
                        placeholder={t('Email/phone')}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.emailOrPhone}
                      />
                      {errors.emailOrPhone && touched.emailOrPhone && (
                        <div className="invalid-feedback">{errors.emailOrPhone}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-12">
                    <div className="custom-form-group auth-form-group d-flex flex-column">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        placeholder={t('sign_in.password')}
                        className={`form-control custom-form-control auth-form-control ${errors.password && touched.password ? 'is-invalid' : ''}`}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.password}
                      />
                      <img
                        className={'hide-icon'}
                        src={showPassword ? eyeSVG : hideEyeSVG}
                        alt="Toggle visibility"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                      {errors.password && touched.password && (
                        <div className="invalid-feedback">{errors.password}</div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="forgot-password-wrapper flex-start mt-2 mb-4">
                  <a href={paths.forgotPassword} className={`forgot-password`}>
                    {t('sign_in.forgot_password')}
                  </a>
                </div>
                <div className="d-flex mt-2 mb-4 justify-content-center">
                  <button
                    type="submit"
                    className="btn btn-primary cursor-pointer"
                    disabled={isSubmitting}
                  >
                    <span className={`submit-btn-text`}> {t('sign_in.login')} </span>
                  </button>
                </div>
                <div className="d-flex mt-3 mb-3 justify-content-center auth-quest">
                  {t('sign_in.exist_account')}
                  <a href={paths.signUp} className={`td-n ml-6`}>
                    {t('sign_up.title')}
                  </a>
                </div>
              </form>
            )}
          </Formik>

          {googleClientId ? (
            <div className="d-flex flex-column align-items-center mt-2 mb-4 gap-2">
              <div className="text-muted small">
                {t('sign_in.login_with_services')}
              </div>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                text="continue_with"
                shape="rectangular"
                width="280"
              />
            </div>
          ) : null}
        </div>
      </div>
    </AuthorizationLayout>
  );
};

export default SignIn;
